import { ApiError } from './api';
import { STABLE_PROBLEM_MESSAGES } from './admin-presentation';

const NATIVE_MESSAGES: Record<string, string> = {
  SLOT_UNAVAILABLE: 'Выбранное время недоступно. Выберите другой слот.',
  HOLD_EXPIRED: 'Время резерва истекло. Выберите слот заново.',
  BOOKING_VERSION_CONFLICT: 'Заявка была изменена. Обновите страницу и повторите.',
  CASE_ALREADY_STARTED: 'Осмотр уже начат — изменить заявку нельзя.',
  CASE_SUPERSEDED: 'Заявка устарела. Откройте актуальную версию.',
  CONFIGURATION_REFRESH_REQUIRED: 'Состав анкеты обновился. Обновите форму перед отправкой.',
  CSRF_DENIED: 'Сеанс безопасности устарел. Войдите снова.',
  FEATURE_DISABLED: 'Запись на осмотр временно недоступна.',
};

const STALE_CODES = new Set([
  'BOOKING_VERSION_CONFLICT',
  'CASE_SUPERSEDED',
  'CONFIGURATION_REFRESH_REQUIRED',
  'STALE_VERSION',
  'VERSION_CONFLICT',
]);

export function isNativeStaleConflict(failure: unknown): boolean {
  if (!(failure instanceof ApiError)) {
    return false;
  }
  const code = failure.problem.code ?? '';
  return STALE_CODES.has(code);
}

export function isNativeValidationError(failure: unknown): boolean {
  return failure instanceof ApiError && (failure.problem.code ?? '') === 'VALIDATION_ERROR';
}

function friendlyFieldMessage(
  code: string | undefined,
  message: string | undefined,
  label: string | undefined,
): string {
  const title = label !== undefined && label.trim() !== '' ? label.trim() : undefined;
  if (code === 'INVALID_TYPE') {
    return title !== undefined
      ? `«${title}» — требуется число`
      : 'Поле должно быть числом';
  }
  if (typeof message === 'string' && message.trim() !== '') {
    // Prefer short Russian when server text is English-typed.
    if (code === 'INVALID_TYPE' || /must be a number/i.test(message)) {
      return title !== undefined
        ? `«${title}» — требуется число`
        : 'Поле должно быть числом';
    }
    return message;
  }
  if (code !== undefined && code.trim() !== '') {
    return code;
  }
  return 'Проверьте значение поля';
}

export function nativeProblemMessage(failure: unknown): string {
  if (failure instanceof ApiError) {
    const code = failure.problem.code ?? '';
    if (code === 'VALIDATION_ERROR') {
      return nativeValidationSummary(failure);
    }
    if (code in NATIVE_MESSAGES) {
      return NATIVE_MESSAGES[code] as string;
    }
    if (code in STABLE_PROBLEM_MESSAGES) {
      return STABLE_PROBLEM_MESSAGES[code] as string;
    }
    if (failure.problem.detail !== undefined && failure.problem.detail.trim() !== '') {
      return failure.problem.detail;
    }
  }
  if (failure instanceof Error && failure.message.trim() !== '') {
    return failure.message;
  }
  return 'Не удалось выполнить операцию.';
}

/** Map Problem.errors[] to per-field messages for the vertical form. */
export function nativeFieldErrors(
  failure: unknown,
  fieldLabels: Readonly<Record<string, string>> = {},
): Readonly<Record<string, string>> {
  if (!(failure instanceof ApiError) || failure.problem.errors === undefined) {
    return {};
  }
  const out: Record<string, string> = {};
  for (const row of failure.problem.errors) {
    if (typeof row.field !== 'string' || row.field.trim() === '') {
      continue;
    }
    out[row.field] = friendlyFieldMessage(row.code, row.message, fieldLabels[row.field]);
  }
  return out;
}

/** Visible summary next to submit/save — lists first invalid field in Russian. */
export function nativeValidationSummary(
  failure: unknown,
  fieldLabels: Readonly<Record<string, string>> = {},
): string {
  if (!(failure instanceof ApiError) || (failure.problem.code ?? '') !== 'VALIDATION_ERROR') {
    return nativeProblemMessage(failure);
  }
  const errors = failure.problem.errors ?? [];
  if (errors.length === 0) {
    return 'Не удалось отправить: проверьте заполнение анкеты.';
  }
  const first = errors[0]!;
  const label =
    typeof first.field === 'string' && first.field.trim() !== ''
      ? (fieldLabels[first.field] ?? first.field)
      : undefined;
  const detail = friendlyFieldMessage(first.code, first.message, label);
  if (errors.length === 1) {
    return `Не удалось отправить: исправьте поле ${detail}`;
  }
  const names = errors
    .map((row) => {
      if (typeof row.field !== 'string' || row.field.trim() === '') {
        return null;
      }
      return fieldLabels[row.field] ?? row.field;
    })
    .filter((x): x is string => x !== null);
  const listed = names
    .slice(0, 3)
    .map((n) => `«${n}»`)
    .join(', ');
  const more = names.length > 3 ? ` и ещё ${names.length - 3}` : '';
  return `Не удалось отправить: исправьте поля ${listed}${more}.`;
}
